import { useEffect, useState, useCallback, useMemo } from "react";
import { GitMerge, X, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

/**
 * Iter-132 — "Possible aliases" card on the Cast hub.
 *
 * Surfaces likely alias relationships detected by the parser:
 *   * modifier_prefix — `FELIX` ↔ `YOUNGER FELIX`
 *   * crowd_index    — `SOLDIER` ↔ `SOLDIER #2`
 *
 * Never auto-merges. Each row offers two calm actions:
 *   1. Review & merge — opens the existing merge dialog with the
 *      pair pre-selected; the user still has to confirm.
 *   2. Keep separate — dismisses the suggestion locally for the
 *      current session (the backend keeps the pair as two records).
 *
 * The card hides itself when there are no suggestions, so the Cast
 * hub stays calm by default.
 */
export default function CastAliasSuggestions({
    projectId,
    onMergeRequest,
    refreshKey,
}) {
    const apiBase = useApiBase();
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(true);
    const [dismissed, setDismissed] = useState(() => new Set());

    const refresh = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/characters/alias-suggestions`,
            );
            setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        } catch (e) {
            setError(formatApiError(e) || "Could not load alias suggestions");
        } finally {
            setLoading(false);
        }
    }, [projectId, apiBase]);

    useEffect(() => {
        refresh();
    }, [refresh, refreshKey]);

    const visible = useMemo(() => {
        // Iter-153b — defensive FE-side validation. Even though the
        // backend filters out alias suggestions missing a base or
        // variant, never render a pair where either side lacks a
        // valid id+name. Avoids the `(no base) ↔ INDIE TEEN`
        // trapped-card UX entirely.
        const isValidSide = (side) =>
            side && side.id && (side.name || "").trim() !== "";
        return suggestions.filter((s) => {
            if (!isValidSide(s?.base) || !isValidSide(s?.variant)) return false;
            const k = pairKey(s);
            return !dismissed.has(k);
        });
    }, [suggestions, dismissed]);

    if (loading) return null;
    if (error) return null;
    if (visible.length === 0) return null;

    const dismiss = (s) => {
        setDismissed((prev) => {
            const next = new Set(prev);
            next.add(pairKey(s));
            return next;
        });
    };

    const startMerge = (s) => {
        // Iter-153b — alias suggestions now guarantee both sides are
        // valid (backend + FE both filter). If somehow the pair is
        // still malformed at click-time (race / stale render), show
        // a recoverable error toast instead of triggering a broken
        // merge dialog. The card stays dismissible — no trapped UI.
        const baseOk = s?.base?.id && (s.base.name || "").trim();
        const variantOk = s?.variant?.id && (s.variant.name || "").trim();
        if (!baseOk || !variantOk) {
            toast.error(
                "Unable to complete merge. Please review the alias pair again.",
            );
            // Auto-dismiss the broken pair so it can't trap the UI.
            dismiss(s);
            return;
        }
        // Modifier-prefix → bare-name FELIX is the natural primary
        // (continuity identity). Crowd-index → bare SOLDIER is primary
        // when present.
        onMergeRequest?.({
            presetPrimaryId: s.base.id,
            presetSecondaryIds: [s.variant.id],
            presetFinalName: s.base.name,
        });
    };

    return (
        <div
            data-testid="cast-alias-suggestions"
            className="mb-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3"
        >
            <div className="flex items-center justify-between gap-2">
                <button
                    type="button"
                    data-testid="cast-alias-suggestions-toggle"
                    onClick={() => setExpanded((v) => !v)}
                    className="tap flex-1 inline-flex items-center gap-2 text-left"
                    aria-expanded={expanded}
                >
                    <Sparkles size={14} className="text-amber-300 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-200/80">
                            Possible aliases
                        </div>
                        <div
                            data-testid="cast-alias-suggestions-count"
                            className="text-xs text-neutral-300 mt-0.5"
                        >
                            {visible.length} pair{visible.length === 1 ? "" : "s"} detected · review to merge
                        </div>
                    </div>
                    {expanded ? (
                        <ChevronUp size={14} className="text-neutral-400" />
                    ) : (
                        <ChevronDown size={14} className="text-neutral-400" />
                    )}
                </button>
            </div>
            {expanded ? (
                <ul
                    data-testid="cast-alias-suggestions-list"
                    className="mt-3 space-y-2"
                >
                    {visible.map((s) => {
                        const k = pairKey(s);
                        return (
                            <li
                                key={k}
                                data-testid={`cast-alias-row-${k}`}
                                className="rounded-lg border border-white/5 bg-card/40 p-2.5 flex items-center gap-2"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-neutral-200 truncate">
                                        <span className="font-medium">
                                            {s.base?.name || "(no base)"}
                                        </span>
                                        <span className="text-neutral-500 mx-1.5">↔</span>
                                        <span className="font-medium">{s.variant.name}</span>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">
                                        {s.reason === "modifier_prefix"
                                            ? `Possible alias${s.modifier ? ` · ${s.modifier.toLowerCase()}` : ""}`
                                            : s.reason === "crowd_index"
                                              ? "Crowd numbering"
                                              : s.reason === "title_abbreviation"
                                                ? `Title abbreviation${s.modifier ? ` · ${s.modifier.toLowerCase()}` : ""}`
                                                : s.reason === "role_prefix"
                                                  ? `Role prefix${s.modifier ? ` · ${s.modifier.toLowerCase()}` : ""}`
                                                  : "Possible alias"}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-testid={`cast-alias-merge-${k}`}
                                    onClick={() => startMerge(s)}
                                    className="tap shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-[11px] font-semibold text-amber-200"
                                    title="Open merge dialog with this pair pre-selected"
                                    disabled={!s.base?.id}
                                >
                                    <GitMerge size={11} />
                                    Review
                                </button>
                                <button
                                    type="button"
                                    data-testid={`cast-alias-dismiss-${k}`}
                                    onClick={() => dismiss(s)}
                                    className="tap shrink-0 h-8 w-8 rounded-md text-neutral-500 hover:text-neutral-200 hover:bg-white/5 inline-flex items-center justify-center"
                                    title="Keep separate (for now)"
                                    aria-label={`Keep ${s.variant.name} separate`}
                                >
                                    <X size={12} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}

function pairKey(s) {
    const a = (s.base?.id || "_") + ":" + (s.variant?.id || "_");
    return `${s.reason}-${a}`;
}
